import { Link } from "react-router-dom";
import { Facebook, Linkedin } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";
import logoDark from "@/assets/logo-dark.webp";

export default function SiteFooter() {
  return (
    <footer className="bg-[var(--navy-deep)] text-white pt-16 pb-6 px-[6vw]" data-testid="site-footer">
      <div className="grid gap-10 md:grid-cols-[2fr_repeat(4,1fr)] max-w-[1350px] mx-auto">
        <div className="max-w-md">
          <img
            src={logoDark}
            alt="Catalyst Behavioral Health Consulting, LLC"
            className="w-full max-w-[340px] h-auto"
            width={340}
            loading="lazy"
          />
          <p className="text-neutral-400 text-xs leading-relaxed mt-4">
            Catalyst helps behavioral healthcare organizations build, grow, and thrive — through advisory
            services, implementation support, digital products, training, and recurring partnerships.
          </p>
          <p className="text-neutral-500 text-xs mt-4">
            Phoenix, Arizona · Serving clients nationwide
          </p>
          <div className="mt-6">
            <NewsletterForm variant="footer" source="footer" />
          </div>
          <div className="mt-6 flex items-center gap-3" data-testid="footer-social">
            <a
              href="https://facebook.com/catalystbhconsulting.com"
              target="_blank" rel="noopener"
              aria-label="Catalyst BH Consulting on Facebook"
              className="w-9 h-9 border border-white/20 hover:border-[var(--gold)] hover:text-[var(--gold)] flex items-center justify-center text-white/80 transition-colors"
              data-testid="footer-facebook"
            >
              <Facebook size={16} />
            </a>
            <a
              href="https://www.linkedin.com/company/catalyst-behavioral-health-consulting"
              target="_blank" rel="noopener"
              aria-label="Catalyst BH Consulting on LinkedIn"
              className="w-9 h-9 border border-white/20 hover:border-[var(--gold)] hover:text-[var(--gold)] flex items-center justify-center text-white/80 transition-colors"
              data-testid="footer-linkedin"
            >
              <Linkedin size={16} />
            </a>
          </div>
        </div>

        <FooterCol title="Divisions" links={[
          { to: "/", label: "BH Consulting" },
          { to: "/hr", label: "HR Consulting" },
          { to: "/continuum", label: "Catalyst Continuum" },
          { to: "/academy", label: "Catalyst Academy" },
        ]} />

        <FooterCol title="Explore" links={[
          { to: "/solutions", label: "Solutions" },
          { to: "/who-we-serve", label: "Who We Serve" },
          { to: "/tools", label: "Free Tools" },
          { to: "/about", label: "About" },
          { to: "/blog", label: "Insights" },
          { to: "/book", label: "Book a Consultation" },
        ]} />

        <FooterCol title="Store" links={[
          { to: "/academy?category=Digital+Downloads", label: "Downloads" },
          { to: "/academy?category=Toolkits", label: "Toolkits" },
          { to: "/academy?category=Courses", label: "Courses" },
          { to: "/academy?category=Memberships", label: "Memberships" },
        ]} />

        <FooterCol title="Contact" links={[
          { href: "tel:6233243237", label: "(623) 324-3237" },
          { href: "mailto:info@catalystbhconsulting.com", label: "info@catalystbhconsulting.com" },
          { to: "/press", label: "Press & Media Kit" },
          { to: "/login", label: "Member Portal" },
        ]} />
      </div>

      <div className="border-t border-white/10 mt-10 pt-6 text-[10px] text-neutral-500 flex flex-col md:flex-row justify-between gap-4">
        <span className="max-w-3xl leading-relaxed">
          © {new Date().getFullYear()} Catalyst Behavioral Health Consulting, LLC. All rights reserved.
          The Catalyst Continuum™, related curricula, workbooks, and materials are the exclusive
          intellectual property of Catalyst Behavioral Health Consulting, LLC and may not be reproduced,
          distributed, or adapted without written permission.
        </span>
        <span className="tracking-widest whitespace-nowrap self-start md:self-end">CATALYSTBHCONSULTING.COM</span>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="text-[var(--gold)] text-[11px] tracking-widest mb-4">{title.toUpperCase()}</h4>
      <ul className="space-y-2">
        {links.map((l, i) => (
          <li key={i}>
            {l.to ? (
              <Link className="text-neutral-400 hover:text-white text-xs" to={l.to}>{l.label}</Link>
            ) : (
              <a className="text-neutral-400 hover:text-white text-xs" href={l.href}>{l.label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
